// schema
import * as ISchema from "@schema";

// libraries
import * as React from "react";
import { withRouter } from "found";
import { connect } from "react-redux";
import { createFragmentContainer } from "react-relay";

// internal
import * as s from "./OrgPricingGrid.styles";
import { IProps, IState, IContainer } from "./OrgPricingGrid.d";
import { orgPricingGridFragments } from "./OrgPricingGrid.gql";

// other components
import { PricingFaq } from "../pricing-faq";
import { H1, P, A } from "@ui";

// util
import {
  ITierType,
  getOrgAccountingPeriodFromOrg,
  getFormattedCycleRangeForOrgAccountingPeriod,
  isPrimarySubscriptionFreePlan,
  isPrimarySubscriptionProPlan,
  isPrimarySubscriptionBusinessPlan,
  isPrimarySubscriptionEnterprisePlan,
  isPrimarySubscriptionNonStandard,
} from "@util/pricing.util";
// import { isAuthed } from '@util/auth.util';
import { track, TrackingEvents } from "@util/analytics.util";
import { windowLocation } from "@util/location.util";

// assets
import {
  brainEmoji,
  briefcaseEmoji,
  // calendarEmoji,
  clipboardEmoji,
  handshakeEmoji,
  linkEmoji,
  lockAndKeyEmoji,
  informationDeskWomanEmoji,
  pencilEmoji,
  rocketEmoji,
  settingsCogEmoji,
  sparkleEmoji,
  silhouetteOfTwoPeopleEmoji,
  whitePaperEmoji,
} from "@img/emojis";

// constants
import {
  IFeatureComparison,
  getFeatureComparisons,
  pricingFaqs,
  freeTier,
  proOrgTier,
  businessTier,
  enterpriseTier,
} from "@constants/org-pricing.constant";
import { supportEmail } from "@clockwise/client-commons/src/constants/site";

import { salesContactFormUrl } from "@constants/site.constant";

// material-ui
import withStyles from "@material-ui/core/styles/withStyles";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Tooltip,
} from "@material-ui/core";
import { AllInclusive, ArrowDropDown, ArrowDropUp, Check, Update } from "@material-ui/icons";

// styles
import { greys } from "@clockwise/web-commons/src/styles/color.styles";
import { TranslationContext } from "@clockwise/client-commons/src/providers/TranslationContext";

const scheduleAssistsTooltipContent =
  "Schedule assists are counted when Clockwise schedules or reschedules intelligently on behalf of a user. It is similar to asking an Executive Assistant to track how many events that they had to move for each user.";

const DEFAULT_SCHEDULE_ASSIST_QUOTA_MAX_PER_WEEK = 75;

class OrgPricingGridComponent extends React.Component<IProps, IState> {
  constructor(props: IProps) {
    super(props);

    this.state = {
      billingRecurrence: props.billingRecurrence
        ? props.billingRecurrence
        : ISchema.SubscriptionInterval.Yearly,
      expandFeatureComparison: true,
      downgradeDialogOpen: false,
    };
  }

  static contextType = TranslationContext;
  declare context: React.ContextType<typeof TranslationContext>;

  // ~-~-~-~-~-~-~-
  // Lifecycle
  // ~-~-~-~-~-~-~-
  // public componentDidMount() {
  //   if (isAuthed()) {
  //     console.log('currently authed!!');
  //   }
  // }

  // ~-~-~-~-~-~-~-
  // Handlers
  // ~-~-~-~-~-~-~-
  // private onBillingRecurrenceChange = (
  //   billingRecurrence: ISchema.SubscriptionInterval,
  // ) => {
  //   const { selectedTeamId } = this.state;

  //   this.setState({ billingRecurrence });
  //   track(TrackingEvents.PRICING.TOGGLE_PRICING_RECURRENCE, {
  //     billingRecurrence,
  //     teamId: selectedTeamId,
  //   });

  //   if (this.props.onChangeBillingRecurrence) {
  //     this.props.onChangeBillingRecurrence(billingRecurrence);
  //   }
  // }

  private goToCheckout = () => {
    const { isDialog } = this.props;

    if (isDialog && this.props.onChangeDialogView) {
      // if dialog, open another dialog
      this.props.onChangeDialogView("Checkout");
    } else {
      // else set new window
      windowLocation.assign("OrgPricingGridGoToCheckout", "/app/checkout");
      this.props.onChangeLoading(true);
    }
  };

  private onToggleFeatureComparison = () => {
    this.setState({ expandFeatureComparison: !this.state.expandFeatureComparison });
    track(TrackingEvents.PRICING.TOGGLE_FEATURE_COMPARISON);
  };

  private onDowngradeClick = (tierType: ITierType) => {
    this.setState({ downgradeDialogOpen: true });
    track(TrackingEvents.PRICING.DOWNGRADE_CLICKED, { tierType });
  };

  private onUpgradeClick = (tierType: ITierType) => {
    this.goToCheckout();
    track(TrackingEvents.PRICING.UPGRADE_CLICKED, { tierType });
  };

  private onContactUsClick = (tierType: ITierType) => {
    track(TrackingEvents.PRICING.CONTACT_US_CLICKED, { tierType });
  };

  private onDowngradeDialogClose = () => {
    this.setState({ downgradeDialogOpen: false });
  };

  private onDowngradeConfirmation = () => {
    track(TrackingEvents.PRICING.DOWNGRADE_CONFIRMATION_CLICKED);
  };

  // ~-~-~-~-~-~-~-
  // Render
  // ~-~-~-~-~-~-~-
  private maybeRenderDowngradeDialog = () => {
    const { classes } = this.props;
    const { downgradeDialogOpen } = this.state;

    return (
      <Dialog
        open={downgradeDialogOpen}
        onClose={this.onDowngradeDialogClose}
        PaperProps={{ style: { maxWidth: 700, width: 700 } }}
      >
        <DialogTitle>
          <H1 style={{ fontSize: 22, marginBottom: 0, paddingBottom: 0 }}>
            Downgrade to the Free Plan?
          </H1>
        </DialogTitle>
        <DialogContent style={{ paddingTop: 0, paddingBottom: 0 }}>
          <P
            style={{
              fontSize: 15,
              lineHeight: 1.8,
              color: greys.slate,
              marginBottom: 30,
            }}
          >
            Our Customer Support team can assist you with downgrading your team to a Clockwise Free
            plan.
          </P>
        </DialogContent>
        <DialogActions>
          <Button
            // keyboardFocused={true}
            variant="text"
            onClick={this.onDowngradeDialogClose}
          >
            Cancel
          </Button>
          <Button
            variant="text"
            onClick={this.onDowngradeConfirmation}
            href={`mailto:${supportEmail}`}
            target="_blank"
            classes={{ root: classes.degradeConfirmationButton }}
          >
            Email Support
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  private maybeRenderNonStandardPlansCallout = () => {
    const { org, classes } = this.props;

    if (!isPrimarySubscriptionNonStandard(org)) {
      return "";
    }

    return (
      <div className={classes.nonStandardPlanCallout}>
        ℹ️ You’re on a paid plan with custom terms. Contact{" "}
        <A href={`mailto:${supportEmail}`} target="_blank">
          {supportEmail}
        </A>{" "}
        to learn more about the details.
      </div>
    );
  };

  private maybeRenderHeader = () => {
    const { org, classes } = this.props;

    const orgAccountingPeriod = getOrgAccountingPeriodFromOrg(org);
    const quotaUsed = (orgAccountingPeriod && orgAccountingPeriod.quotaUsed) || 0;

    return (
      <div className={classes.headerContainer}>
        <div className={classes.headerRow}>
          <H1
            style={{
              fontSize: 32,
              lineHeight: "32px",
              display: "flex",
              alignItems: "center",
              marginBottom: 0,
              paddingBottom: 20,
              textAlign: "center",
            }}
          >
            Choose a plan for your organization
          </H1>
        </div>
        {isPrimarySubscriptionFreePlan(org) ? (
          <div className={classes.headerUsageSummary}>
            Your organization has used{" "}
            <span style={{ fontWeight: "bold" }}>{quotaUsed} schedule assists</span> in the last
            two-week cycle, {getFormattedCycleRangeForOrgAccountingPeriod(orgAccountingPeriod)}.
            <br />
            We recommend you upgrade to the Pro plan.
          </div>
        ) : (
          ""
        )}
      </div>
    );
  };

  private renderTierAction = (tierType: ITierType) => {
    const { classes, org } = this.props;

    const isFreePlan = isPrimarySubscriptionFreePlan(org);
    const isProPlan = isPrimarySubscriptionProPlan(org);
    const isBusinessPlan = isPrimarySubscriptionBusinessPlan(org);
    const isEnterprisePlan = isPrimarySubscriptionEnterprisePlan(org);

    const isBusinessOrEnterprisePlan = isBusinessPlan || isEnterprisePlan;

    switch (tierType) {
      // ~~~~~~~~~~~~~~~~~
      // Free plan actions
      // ~~~~~~~~~~~~~~~~~
      case "FreemiumOrg":
        return (
          <Button
            onClick={() => this.onDowngradeClick(tierType)}
            className={`${classes.tierButton} ${classes.secondaryButton} ${
              isFreePlan ? classes.disabledButton : ""
            }`}
            variant="outlined"
          >
            {isFreePlan ? "Current plan" : "Downgrade"}
          </Button>
        );
      // ~~~~~~~~~~~~~~~~~
      // Pro plan actions
      // ~~~~~~~~~~~~~~~~~
      case "ProOrg":
        return (
          <Button
            onClick={() => this.onUpgradeClick(tierType)}
            className={`${classes.tierButton} ${
              isBusinessOrEnterprisePlan ? classes.secondaryButton : classes.primaryButton
            } ${isFreePlan ? "" : classes.disabledButton}`}
            variant="outlined"
            color={isBusinessOrEnterprisePlan ? undefined : "primary"}
          >
            {isProPlan ? "Current plan" : "Select"}
          </Button>
        );
      // ~~~~~~~~~~~~~~~~~
      // Business plan action
      // ~~~~~~~~~~~~~~~~~
      case "BusinessOrg":
        return (
          <Button
            onClick={() => this.onContactUsClick(tierType)}
            href={salesContactFormUrl}
            target="_blank"
            className={`${classes.tierButton} ${classes.primaryContactUs} ${
              isBusinessPlan ? classes.disabledButton : ""
            }`}
            variant="outlined"
            classes={{ label: classes.primaryContactUsLabel }}
          >
            {isBusinessPlan ? "Current plan" : "Contact us"}
          </Button>
        );
      // ~~~~~~~~~~~~~~~~~
      // Enterprise plan action
      // ~~~~~~~~~~~~~~~~~
      case "Enterprise":
        return (
          <Button
            onClick={() => this.onContactUsClick(tierType)}
            href={salesContactFormUrl}
            target="_blank"
            className={`${classes.tierButton} ${classes.primaryContactUs} ${
              isEnterprisePlan ? classes.disabledButton : ""
            }`}
            variant="outlined"
            classes={{ label: classes.primaryContactUsLabel }}
          >
            {isEnterprisePlan ? "Current plan" : "Contact us"}
          </Button>
        );
      default:
        return "";
    }
  };

  private renderFreeTierHeader = () => {
    const { classes } = this.props;
    const { billingRecurrence } = this.state;

    const costForRecurrence =
      billingRecurrence === ISchema.SubscriptionInterval.Yearly
        ? freeTier.planPricing.yearlyCost
        : freeTier.planPricing.monthlyCost;

    return (
      <div key={`tier-free`} className={classes.tier}>
        <div className={classes.tierTitle}>Free</div>
        <div className={classes.tierCost}>
          <span className={classes.dollar}>$</span>
          <span>{costForRecurrence}</span>
          <span className={classes.currency}>USD</span>
        </div>
        <div className={classes.tierCostUnit}>{freeTier.costUnit}</div>
        {this.renderTierAction(freeTier.type)}
        <div className={classes.tierDescriptionShort}>For teams trialing Clockwise</div>
        <Divider classes={{ root: classes.divider }} />
        <div className={classes.tierFeatureHeader}>{freeTier.featureHeader}</div>
        <div className={classes.tierFeature}>
          <div className={classes.tierFeatureBullet} style={{ marginLeft: 5 }}>
            <img src={sparkleEmoji} className={classes.emoji} />
          </div>
          <div>
            <span className={classes.green}>{DEFAULT_SCHEDULE_ASSIST_QUOTA_MAX_PER_WEEK}</span>{" "}
            <Tooltip placement="top" title={scheduleAssistsTooltipContent}>
              <span className={classes.featureTitleUnderline}>Schedule Assists</span>
            </Tooltip>{" "}
            per week
          </div>
        </div>
        <div className={classes.tierFeature}>
          <div className={classes.tierFeatureBullet}>
            <img src={rocketEmoji} className={classes.emoji} />
          </div>
          <div>Team productivity tools</div>
        </div>
        <div className={classes.tierFeature}>
          <div className={classes.tierFeatureBullet}>
            <img src={linkEmoji} className={classes.emoji} />
          </div>
          <div>Unlimited scheduling links</div>
        </div>
      </div>
    );
  };

  private renderProTierHeader = () => {
    const { classes, org } = this.props;
    const { billingRecurrence } = this.state;

    const proTier = proOrgTier;
    const costForRecurrence =
      billingRecurrence === ISchema.SubscriptionInterval.Yearly
        ? proTier.planPricing.yearlyCost
        : proTier.planPricing.monthlyCost;

    return (
      <div key={`tier-pro`} className={classes.tier}>
        {isPrimarySubscriptionFreePlan(org) ? (
          <div className={classes.popularBanner}>Popular</div>
        ) : (
          ""
        )}
        <div className={classes.tierTitle}>{proOrgTier.title}</div>
        <div className={classes.tierCost}>
          <span className={classes.dollar}>$</span>
          <span>
            <span>{costForRecurrence.toString()[0]}</span>
            <span style={{ fontSize: 26 }}>{costForRecurrence.toString().slice(1)}</span>
          </span>
          <div className={classes.currency}>USD</div>
        </div>
        <div className={classes.tierCostUnit}>{proOrgTier.costUnit}</div>
        {this.renderTierAction(proOrgTier.type)}
        <div className={classes.tierDescription}>
          For teams that want limitless schedule optimization.
        </div>
        <Divider classes={{ root: classes.divider }} />
        <div className={classes.tierFeatureHeader}>{proOrgTier.featureHeader}</div>
        <div className={classes.tierFeature}>
          <div className={classes.tierFeatureBullet} style={{ marginLeft: 5 }}>
            <img src={sparkleEmoji} className={classes.emoji} />
          </div>
          <div>
            <span className={classes.green}>Unlimited</span>{" "}
            <Tooltip placement="top" title={scheduleAssistsTooltipContent}>
              <span className={classes.featureTitleUnderline}>Schedule Assists</span>
            </Tooltip>
          </div>
        </div>
        <div className={classes.tierFeature}>
          <div className={classes.tierFeatureBullet}>
            <img src={brainEmoji} className={classes.emoji} />
          </div>
          <div>Advanced calendar controls</div>
        </div>
        {/* <div className={classes.tierFeature}>
          <div className={classes.tierSubFeature}>{t("pricing:pro-tier-autopilot")}</div>
        </div>
        <div className={classes.tierFeature}>
          <div className={classes.tierSubFeature}>Meeting breaks</div>
        </div> */}
        <div className={classes.tierFeature}>
          <div className={classes.tierFeatureBullet}>
            <img src={handshakeEmoji} className={classes.emoji} />
          </div>
          <div>Priority support</div>
        </div>
      </div>
    );
  };

  private renderBusinessTierHeader = () => {
    const { classes } = this.props;
    const { billingRecurrence } = this.state;

    const costForRecurrence = (billingRecurrence === ISchema.SubscriptionInterval.Yearly
      ? businessTier.planPricing.yearlyCost
      : businessTier.planPricing.monthlyCost
    ).toPrecision(4);

    return (
      <div key={`tier-business`} className={classes.tier}>
        <div className={classes.tierTitle}>{businessTier.title}</div>
        <div className={classes.tierCost}>
          <span className={classes.dollar}>$</span>
          <span>
            <span>{costForRecurrence.toString().substring(0, 2)}</span>
            <span style={{ fontSize: 26 }}>{costForRecurrence.toString().slice(2)}</span>
          </span>
          <span className={classes.currency}>USD</span>
        </div>
        <div className={classes.tierCostUnit}>{businessTier.costUnit}</div>
        {this.renderTierAction(businessTier.type)}
        <div className={classes.tierDescriptionShort}>
          For teams looking to centralize management.
        </div>
        <Divider classes={{ root: classes.divider }} />
        <div className={classes.tierFeatureHeader}>{businessTier.featureHeader}</div>
        <div className={classes.tierFeature}>
          <div className={classes.tierFeatureBullet}>
            <img src={settingsCogEmoji} className={classes.emoji} />
          </div>
          <div>Admin controls and user roles</div>
        </div>
        <div className={classes.tierFeature}>
          <div className={classes.tierFeatureBullet}>
            <img src={briefcaseEmoji} className={classes.emoji} />
          </div>
          <div>Personalized onboarding and training</div>
        </div>
        <div className={classes.tierFeature}>
          <div className={classes.tierFeatureBullet}>
            <img src={informationDeskWomanEmoji} className={classes.emoji} />
          </div>
          <div>Dedicated Customer Success Manager</div>
        </div>
        <div className={classes.tierFeature}>
          <div className={classes.tierFeatureBullet}>
            <img src={pencilEmoji} className={classes.emoji} />
          </div>
          <div>Meeting culture workshop</div>
        </div>
        <div className={classes.tierFeature}>
          <div className={classes.tierFeatureBullet}>
            <img src={clipboardEmoji} className={classes.emoji} />
          </div>
          <div>Custom contracts & invoicing</div>
        </div>
      </div>
    );
  };

  private renderEnterpriseTierHeader = () => {
    const { classes } = this.props;
    // const t = this.context;

    return (
      <div key={`tier-enterprise`} className={classes.tier}>
        <div className={classes.tierTitle}>Enterprise</div>
        <div className={classes.tierCost}>
          <span style={{ fontSize: 24, alignSelf: "flex-end" }}>Let's Talk!</span>
        </div>
        <div className={classes.tierCostUnit}>{enterpriseTier.costUnit}</div>
        {this.renderTierAction(enterpriseTier.type)}
        <div className={classes.tierDescription}>
          For organizations that require additional control and security options.
        </div>
        <Divider classes={{ root: classes.divider }} />
        <div className={classes.tierFeatureHeader}>{enterpriseTier.featureHeader}</div>
        <div className={classes.tierFeature}>
          <div className={classes.tierFeatureBullet}>
            <img src={lockAndKeyEmoji} className={classes.emoji} />
          </div>
          <div>Advanced privacy options</div>
        </div>
        <div className={classes.tierFeature}>
          <div className={classes.tierFeatureBullet}>
            <img src={silhouetteOfTwoPeopleEmoji} className={classes.emoji} />
          </div>
          <div>User provisioning (SCIM)</div>
        </div>
        <div className={classes.tierFeature}>
          <div className={classes.tierFeatureBullet}>
            <img src={whitePaperEmoji} className={classes.emoji} />
          </div>
          <div>Preferred Support & Maintenance</div>
        </div>
      </div>
    );
  };

  private renderPricingTierHeaders = () => {
    const { classes } = this.props;

    return (
      <React.Fragment>
        <div className={classes.pricingGridContainer}>
          {this.renderFreeTierHeader()}
          {this.renderProTierHeader()}
          {this.renderBusinessTierHeader()}
          {this.renderEnterpriseTierHeader()}
        </div>
      </React.Fragment>
    );
  };

  private renderFeatureComparison = (featureComparison: IFeatureComparison, i: number) => {
    const { classes } = this.props;
    return (
      <div key={`feature-comparison-${i}`} className={classes.featureComparisonRow}>
        <div
          className={`${classes.featureTitle} ${
            featureComparison.isSectionHeader ? classes.featureTitleBorder : ""
          }`}
        >
          <Tooltip
            placement="top"
            title={`${featureComparison.tooltip}`}
            open={!featureComparison.tooltip ? false : undefined}
          >
            <span
              className={
                featureComparison.isSectionHeader
                  ? classes.featureTitleBold
                  : featureComparison.tooltip
                  ? classes.featureTitleUnderline
                  : undefined
              }
            >
              {featureComparison.feature}
            </span>
          </Tooltip>
        </div>
        <div
          className={`${classes.freeFeature} ${
            featureComparison.isSectionHeader ? classes.featureTitleBorder : ""
          }`}
        >
          {featureComparison.isSectionHeader ? (
            ""
          ) : featureComparison.tierFeatureMap.free.description ? (
            featureComparison.tierFeatureMap.free.description
          ) : featureComparison.tierFeatureMap.free.enabled === "Enabled" ? (
            <Check classes={{ root: classes.grey }} />
          ) : featureComparison.tierFeatureMap.free.enabled === "Disabled" ? (
            // <Close classes={{ root: classes.red }} />
            <div />
          ) : featureComparison.tierFeatureMap.free.enabled === "Unlimited" ? (
            <AllInclusive classes={{ root: classes.grey }} />
          ) : featureComparison.tierFeatureMap.free.enabled === "Limited" ? (
            <Update classes={{ root: classes.red }} />
          ) : (
            ""
          )}
        </div>
        <div
          className={`${classes.proFeature} ${
            featureComparison.isSectionHeader ? classes.featureTitleBorder : ""
          }`}
        >
          {featureComparison.isSectionHeader ? (
            ""
          ) : featureComparison.tierFeatureMap.pro.description ? (
            featureComparison.tierFeatureMap.pro.description
          ) : featureComparison.tierFeatureMap.pro.enabled === "Enabled" ? (
            <Check classes={{ root: classes.grey }} />
          ) : featureComparison.tierFeatureMap.pro.enabled === "Disabled" ? (
            // <Close classes={{ root: classes.red }} />
            <div />
          ) : featureComparison.tierFeatureMap.pro.enabled === "Unlimited" ? (
            <AllInclusive classes={{ root: classes.grey }} />
          ) : featureComparison.tierFeatureMap.pro.enabled === "Limited" ? (
            <Update classes={{ root: classes.red }} />
          ) : (
            ""
          )}
        </div>
        <div
          className={`${classes.businessFeature} ${
            featureComparison.isSectionHeader ? classes.featureTitleBorder : ""
          }`}
        >
          {featureComparison.isSectionHeader ? (
            ""
          ) : featureComparison.tierFeatureMap.business.description ? (
            featureComparison.tierFeatureMap.business.description
          ) : featureComparison.tierFeatureMap.business.enabled === "Enabled" ? (
            <Check classes={{ root: classes.grey }} />
          ) : featureComparison.tierFeatureMap.business.enabled === "Disabled" ? (
            // <Close classes={{ root: classes.red }} />
            <div />
          ) : featureComparison.tierFeatureMap.business.enabled === "Unlimited" ? (
            <AllInclusive classes={{ root: classes.grey }} />
          ) : featureComparison.tierFeatureMap.business.enabled === "Limited" ? (
            <Update classes={{ root: classes.red }} />
          ) : (
            ""
          )}
        </div>
        <div
          className={`${classes.enterpriseFeature} ${
            featureComparison.isSectionHeader ? classes.featureTitleBorder : ""
          }`}
        >
          {featureComparison.isSectionHeader ? (
            ""
          ) : featureComparison.tierFeatureMap.enterprise.description ? (
            featureComparison.tierFeatureMap.enterprise.description
          ) : featureComparison.tierFeatureMap.enterprise.enabled === "Enabled" ? (
            <Check classes={{ root: classes.grey }} />
          ) : featureComparison.tierFeatureMap.enterprise.enabled === "Disabled" ? (
            // <Close classes={{ root: classes.red }} />
            <div />
          ) : featureComparison.tierFeatureMap.enterprise.enabled === "Unlimited" ? (
            <AllInclusive classes={{ root: classes.grey }} />
          ) : featureComparison.tierFeatureMap.enterprise.enabled === "Limited" ? (
            <Update classes={{ root: classes.red }} />
          ) : (
            ""
          )}
        </div>
      </div>
    );
  };

  private renderFeatureComparisonGrid = () => {
    const { classes } = this.props;
    const { expandFeatureComparison } = this.state;
    const t = this.context;

    return (
      <React.Fragment>
        <div className={classes.featureComparisonToggle} onClick={this.onToggleFeatureComparison}>
          <div>{expandFeatureComparison ? "Hide" : "See"} feature comparison</div>
          {expandFeatureComparison ? (
            <ArrowDropUp classes={{ root: classes.featureComparisonToggleIcon }} />
          ) : (
            <ArrowDropDown classes={{ root: classes.featureComparisonToggleIcon }} />
          )}
        </div>
        <div
          className={`${classes.featureComparisonContainer} ${
            expandFeatureComparison ? classes.expanded : classes.collapsed
          }`}
        >
          <div className={classes.featureComparisonRow}>
            <div className={`${classes.featureTitle} ${classes.featureHeader}`}>
              <div className={classes.tierFeatureTitleContainer}>
                <div className={`${classes.tierFeatureTitle} ${classes.featureTitleBold}`}>
                  <img src={sparkleEmoji} className={classes.emoji} /> Smart calendar management
                </div>
                <div className={classes.tierFeatureSubtitle}>
                  Features powered by{" "}
                  <Tooltip placement="top" title={scheduleAssistsTooltipContent}>
                    <span className={classes.featureTitleUnderline}>Schedule Assists</span>
                  </Tooltip>{" "}
                </div>
              </div>
            </div>
            <div className={`${classes.freeFeature} ${classes.featureHeader}`}>Free</div>
            <div className={`${classes.proFeature} ${classes.featureHeader}`}>Pro</div>
            <div className={`${classes.businessFeature} ${classes.featureHeader}`}>Business</div>
            <div className={`${classes.enterpriseFeature} ${classes.featureHeader}`}>
              Enterprise
            </div>
          </div>
          <div className={classes.featureComparisonRow} style={{ height: "100%" }}>
            <div className={classes.featureTitle}></div>
            <div className={classes.freeFeature}>
              <div className={classes.scheduleAssistsRow}>
                <Update className={`${classes.scheduleAssistsIcon} ${classes.red}`} />
                <div className={classes.red}>
                  {DEFAULT_SCHEDULE_ASSIST_QUOTA_MAX_PER_WEEK} weekly
                  <br />{" "}
                  <Tooltip placement="top" title={scheduleAssistsTooltipContent}>
                    <span className={`${classes.featureTitleUnderline} ${classes.borderRed}`}>
                      Schedule Assists
                    </span>
                  </Tooltip>{" "}
                  for your organization
                </div>
              </div>
            </div>
            <div className={classes.proFeature}>
              <div className={classes.scheduleAssistsRow}>
                <AllInclusive className={`${classes.scheduleAssistsIcon} ${classes.grey}`} />
                <div className={classes.grey}>
                  Unlimited
                  <br />{" "}
                  <Tooltip placement="top" title={scheduleAssistsTooltipContent}>
                    <span className={classes.featureTitleUnderline}>Schedule Assists</span>
                  </Tooltip>{" "}
                  for your organization
                </div>
              </div>
            </div>
            <div className={classes.businessFeature}>
              <div className={classes.scheduleAssistsRow}>
                <AllInclusive className={`${classes.scheduleAssistsIcon} ${classes.grey}`} />
                <div className={classes.grey}>
                  Unlimited
                  <br />{" "}
                  <Tooltip placement="top" title={scheduleAssistsTooltipContent}>
                    <span className={classes.featureTitleUnderline}>Schedule Assists</span>
                  </Tooltip>{" "}
                  for your organization
                </div>
              </div>
            </div>
            <div className={classes.enterpriseFeature}>
              <div className={classes.scheduleAssistsRow}>
                <AllInclusive className={`${classes.scheduleAssistsIcon} ${classes.grey}`} />
                <div className={classes.grey}>
                  Unlimited
                  <br />{" "}
                  <Tooltip placement="top" title={scheduleAssistsTooltipContent}>
                    <span className={classes.featureTitleUnderline}>Schedule Assists</span>
                  </Tooltip>{" "}
                  for your organization
                </div>
              </div>
            </div>
          </div>
          {getFeatureComparisons({ t }).map(this.renderFeatureComparison)}
          <div className={classes.featureComparisonRow} style={{ height: 70 }}>
            <div className={classes.featureTitle} />
            <div className={classes.baseFeature}>{this.renderTierAction("FreemiumOrg")}</div>
            <div className={classes.baseFeature}>{this.renderTierAction("ProOrg")}</div>
            <div className={classes.baseFeature}>{this.renderTierAction("BusinessOrg")}</div>
            <div className={classes.baseFeature}>{this.renderTierAction("Enterprise")}</div>
          </div>
        </div>
      </React.Fragment>
    );
  };

  private renderFAQ = () => {
    const { classes } = this.props;
    return (
      <div className={classes.faqContainer}>
        <H1 style={{ textAlign: "center", fontSize: 28, padding: 0, marginBottom: 40 }}>
          Top FAQs
        </H1>
        {pricingFaqs.map((faq, i) => (
          <PricingFaq key={i} question={faq.question} answer={faq.answer} />
        ))}
      </div>
    );
  };

  public render() {
    return (
      <div
        className={this.props.classes.wrapper}
        style={!this.props.isDialog ? { position: "relative" } : undefined}
      >
        <div className={this.props.classes.container}>
          {this.maybeRenderNonStandardPlansCallout()}
          {this.maybeRenderHeader()}
          {this.renderPricingTierHeaders()}
          {this.renderFeatureComparisonGrid()}
          {this.renderFAQ()}
          {this.maybeRenderDowngradeDialog()}
        </div>
      </div>
    );
  }
}

/////////////////
// With Styles //
/////////////////

const OrgPricingGridStyled = withStyles(s.styles)(OrgPricingGridComponent);

///////////
// Redux //
///////////

function mapStateToProps(_state: any, _ownProps: IContainer) {
  return {}; // add redux state here, if needed
}

const reduxContainer = connect(mapStateToProps, {})(withRouter(OrgPricingGridStyled));

///////////
// Relay //
///////////

export const OrgPricingGrid = createFragmentContainer<IContainer>(
  reduxContainer,
  orgPricingGridFragments,
);
